import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BusinessUnitInterface,
  DeliveryEnum,
  PaymentModeId,
  PieceCategoryInterface,
  TaxIdentificationTypeInterface,
} from '../interfaces'
import { LocationDto } from '../interfaces/Dto/LocationDto'
import { BUByLocationDto } from '../interfaces/Location/LocationByBu'
import { RangeDeclareDto } from '../interfaces/General/RangelDeclareDto'
import { ServiceDTO } from '../interfaces/Services/Services'
import { DestinationInterface } from '../interfaces/Account/DestinationInterface'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { hydrateBulkCatalogs } from '../store/slices/bulkCatalogs'
import { setOriginBusinessUnits } from '../store/slices'
import {
  getFavoriteAndRecentDestinationsDjango,
  getOriginBusinessUnits,
} from '../services'
import { useAllowedServices } from './useAllowedServices'
import { useContractDestinationBUs } from './useContractDestinationBUs'
import {
  augmentCatalogWithContractStores,
  resolveDestinationBusinessUnits,
} from '../utils/destinationBusinessUnits'
import { authorizedServiceOptions } from '../utils/authorizedServices'

/**
 * Reúne TODO lo que hace falta para generar la plantilla Excel de carga masiva.
 *
 * Nació de `ShipmentBulkUpload` (la pantalla de creación), que era la única que la
 * generaba. Desde que el detalle de una carga también baja la plantilla —prellenada con
 * las guías que fallaron— hay dos pantallas que necesitan los mismos ~8 catálogos, y
 * duplicar ese cableado era garantizar que se desincronizara: si una arma el desplegable
 * de tiendas con el catálogo del contrato y la otra con el catálogo completo, el archivo
 * de una ofrece destinos que la otra rechaza.
 *
 * No incluye lo que es propio del flujo de creación (validación inicial de setup,
 * diagnóstico de servicios, parsing del archivo): eso se queda en la pantalla.
 */

export interface BulkTemplateArgs {
  /**
   * Los 11 argumentos posicionales de `exportFormatoShipmentXLSX_V2WithCoords`, en orden y
   * listos para hacer spread. Evita que cada call site los vuelva a ordenar a mano.
   */
  templateArgs: [
    TaxIdentificationTypeInterface[],
    { value: string; name: string }[], // typeShipment
    BusinessUnitInterface[], // businessUnitOptions (destinos del contrato)
    LocationDto[], // citiesType
    PieceCategoryInterface[],
    boolean, // isCOD para la plantilla (¡ojo!, ver isCODForTemplate)
    DestinationInterface[], // favoriteDestinations
    RangeDeclareDto[],
    number | undefined, // defaultServiceId
    BusinessUnitInterface[], // originBusinessUnits
    ServiceDTO[],
  ]
  /** Catálogos crudos, para el mapper de prellenado y para el Review. */
  catalogs: {
    identificationTypes: TaxIdentificationTypeInterface[]
    businessUnits: BusinessUnitInterface[]
    netBusinessUnits: BusinessUnitInterface[]
    /** Catálogo Django completado con las tiendas virtuales del contrato. */
    catalogForContract: BusinessUnitInterface[]
    /** Tiendas DESTINO que el contrato permite (las del desplegable de la plantilla). */
    businessUnitOptions: BusinessUnitInterface[]
    citiesType: LocationDto[]
    citiesByBu: BUByLocationDto[]
    pieceCategories: PieceCategoryInterface[]
    favoriteDestinations: DestinationInterface[]
    rangeDeclareList: RangeDeclareDto[]
    allowedServices: ServiceDTO[]
    serviceOptions: ServiceDTO[]
    defaultServiceId?: number
    originBusinessUnits: BusinessUnitInterface[]
    typeShipment: { value: string; name: string }[]
  }
  /** ¿Se puede generar una plantilla completa y correcta ya mismo? */
  ready: boolean
  /** Por qué no, en texto listo para un tooltip. `null` cuando `ready`. */
  notReadyReason: string | null
  flags: {
    catalogsLoading: boolean
    catalogsError: boolean
    catalogsLoaded: boolean
    favoritesLoaded: boolean
    servicesLoaded: boolean
    /** Sin saber qué tiendas permite el contrato, la plantilla saldría con el catálogo completo. */
    templateBlocked: boolean
    contractStatus: string
  }
  /**
   * COD del REVIEW: modalidad de pago 2. NO es el mismo booleano que va a la plantilla.
   * Ver `isCODForTemplate`.
   */
  isCODForReview: boolean
  /**
   * COD de la PLANTILLA: es `!hasCredit`, o sea "la línea NO tiene crédito (modalidad 3)".
   * Bloquea las columnas de Remitente en el Excel.
   *
   * Se expone aparte porque en la pantalla de creación conviven los dos booleanos con el
   * mismo nombre y valores distintos (modalidad 2 vs. no-modalidad-3). Pasar el de Review
   * a la plantilla desbloquearía las columnas del remitente para una línea que no puede
   * editarlas — y al revés las bloquearía para una que sí.
   */
  isCODForTemplate: boolean
  retryCatalogs: () => void
}

/**
 * @param enabled Con `false` el hook no despacha nada ni pide favoritos. El detalle lo usa
 * así para que una carga sin errores no dispare cuatro peticiones que nadie va a usar.
 */
export const useBulkTemplateArgs = (
  { enabled = true }: { enabled?: boolean } = {}
): BulkTemplateArgs => {
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)

  // Carga masiva: tiendas, ciudades, categorías y la relación ciudad→tienda salen del
  // sub-slice Django (fresco por sesión), no de inmutable (.NET). identificationTypes sigue
  // viniendo de .NET.
  const businessUnits = useAppSelector(
    (state) => state.bulkCatalogs.businessUnits
  )
  const citiesType = useAppSelector((state) => state.bulkCatalogs.cities)
  const citiesByBu = useAppSelector((state) => state.bulkCatalogs.citiesByBu)
  const pieceCategories = useAppSelector(
    (state) => state.bulkCatalogs.pieceCategories
  )
  const catalogsLoading = useAppSelector((state) => state.bulkCatalogs.loading)
  const catalogsError = useAppSelector((state) => state.bulkCatalogs.error)
  const catalogsLoaded = useAppSelector((state) => state.bulkCatalogs.loaded)
  const identificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  )
  // Catálogo .NET, solo como respaldo para las tiendas del contrato que el de Django no
  // trae (las virtuales). Ver augmentCatalogWithContractStores.
  const netBusinessUnits = useAppSelector(
    (state) => state.inmutable.businessUnits
  )
  const rangeDeclareList = useAppSelector(
    (state) => state.inmutable.rangeDeclareList
  )

  // Tiendas destino que el contrato permite (AgreementBU). MISMO hook y MISMA regla que
  // guía, preenvío, favoritos e import legacy.
  const { rows: contractDestinationRows, status: contractStatus } =
    useContractDestinationBUs(user.client?.agreementID)

  const catalogForContract = useMemo(
    () =>
      augmentCatalogWithContractStores(
        businessUnits,
        netBusinessUnits,
        contractDestinationRows
      ),
    [businessUnits, netBusinessUnits, contractDestinationRows]
  )

  const { options: businessUnitOptions } = useMemo(
    () =>
      resolveDestinationBusinessUnits(
        catalogForContract,
        contractDestinationRows,
        new Date()
      ),
    [catalogForContract, contractDestinationRows]
  )

  const paymentModeRaw =
    user.paymentMethod?.[0]?.paymentModeID ?? PaymentModeId.CREDIT
  const { allowedServices, loaded: servicesLoaded } = useAllowedServices(
    paymentModeRaw as PaymentModeId
  )
  const serviceOptions = useMemo(
    () => authorizedServiceOptions(allowedServices, user.client, paymentModeRaw),
    [allowedServices, user.client, paymentModeRaw]
  )
  const defaultServiceId = serviceOptions?.[0]?.serviceID

  // Hidrata los catálogos Django una vez por sesión. `hydrateBulkCatalogs` ya se guarda de
  // no repetir el fetch si están cargados o cargando, así que llamarlo desde dos pantallas
  // distintas es inocuo.
  useEffect(() => {
    if (!enabled) return
    dispatch(hydrateBulkCatalogs())
  }, [dispatch, enabled])

  /**
   * [D8] Sesión vieja sin la lista de oficinas origen: si nunca se buscó (`undefined`) y
   * hay contrato, se pide acá.
   *
   * Vive en el hook y no en la pantalla de creación porque el detalle tiene el mismo
   * problema: sin la lista, la plantilla sale SIN la columna "Oficina origen" aunque el
   * contrato tenga varias, y todas las guías corregidas volverían con la oficina por
   * defecto. El ref evita repetir el fetch si la respuesta tampoco define la lista.
   */
  const originsFetchRequested = useRef(false)
  useEffect(() => {
    if (!enabled || !user) return
    if (user.originBusinessUnits !== undefined) return
    if (!user.client?.agreementID) return
    if (originsFetchRequested.current) return
    originsFetchRequested.current = true
    getOriginBusinessUnits(user.client.agreementID).then((list) => {
      dispatch(setOriginBusinessUnits(list))
    })
  }, [enabled, user, dispatch])

  // Carga masiva-only: OFFICE se muestra como "Tienda" (consistente con la plantilla
  // Excel). El valor interno sigue siendo DeliveryEnum.OFFICE.
  const typeShipment = useMemo(
    () => [
      { value: DeliveryEnum.OFFICE, name: 'Tienda' },
      { value: DeliveryEnum.DELIVERY, name: 'Domicilio' },
    ],
    []
  )

  // Favoritos: alimentan la hoja oculta del Excel (dropdown + autocompletado) y el Review.
  const [favoriteDestinations, setFavoriteDestinations] = useState<
    DestinationInterface[]
  >([])
  const [favoritesLoaded, setFavoritesLoaded] = useState(false)
  useEffect(() => {
    if (!enabled) return
    const accountId = user?.client?.id
    if (!accountId || favoritesLoaded) return
    getFavoriteAndRecentDestinationsDjango(
      accountId,
      identificationTypes,
      catalogForContract,
      ''
    )
      .then((res) => {
        if (!res.didError && res.model) {
          setFavoriteDestinations(res.model.favoriteDestinations || [])
        }
      })
      .catch(() => {
        /* silencioso — sin favoritos la plantilla solo pierde el dropdown */
      })
      .finally(() => setFavoritesLoaded(true))
  }, [
    enabled,
    user?.client?.id,
    identificationTypes,
    catalogForContract,
    favoritesLoaded,
  ])

  // Mientras no sepamos qué permite el contrato no se genera la plantilla: el Excel lleva
  // la lista embebida como validación de datos y el cliente lo llena sin conexión, así que
  // uno bajado a medias reparte el catálogo completo.
  const templateBlocked = contractStatus !== 'ok'

  const hasCredit = !!user.paymentMethod?.find(
    (x: any) => x.paymentModeID?.toString() === '3'
  )
  const isCODForTemplate = !hasCredit
  const isCODForReview =
    user.paymentMethod?.some((m: any) => m.paymentModeID?.toString() === '2') ??
    false

  const notReadyReason = catalogsError
    ? 'No se pudieron cargar los catálogos. Reintentá.'
    : !catalogsLoaded
      ? 'Cargando catálogos…'
      : templateBlocked
        ? 'Cargando las tiendas de tu contrato…'
        : !servicesLoaded
          ? 'Cargando servicios…'
          : !favoritesLoaded
            ? 'Cargando destinatarios favoritos…'
            : null

  return {
    templateArgs: [
      identificationTypes,
      typeShipment,
      businessUnitOptions,
      citiesType,
      pieceCategories,
      isCODForTemplate,
      favoriteDestinations,
      rangeDeclareList,
      defaultServiceId,
      user.originBusinessUnits ?? [],
      serviceOptions,
    ],
    catalogs: {
      identificationTypes,
      businessUnits,
      netBusinessUnits,
      catalogForContract,
      businessUnitOptions,
      citiesType,
      citiesByBu,
      pieceCategories,
      favoriteDestinations,
      rangeDeclareList,
      allowedServices,
      serviceOptions,
      defaultServiceId,
      originBusinessUnits: user.originBusinessUnits ?? [],
      typeShipment,
    },
    ready: notReadyReason === null,
    notReadyReason,
    flags: {
      catalogsLoading,
      catalogsError,
      catalogsLoaded,
      favoritesLoaded,
      servicesLoaded,
      templateBlocked,
      contractStatus,
    },
    isCODForReview,
    isCODForTemplate,
    retryCatalogs: () => dispatch(hydrateBulkCatalogs(true)),
  }
}
